import { Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Guia do Parceiro reutilizavel.
 *
 * Props:
 * - partnerName: nome real ou "Sua Empresa Parceira" (para versao generica)
 * - partnerSlug: slug real ou "seu-slug" (placeholder)
 * - partnerToken: token real ou "SEU-TOKEN-DE-ACESSO" (placeholder)
 * - supportWhatsapp / supportEmail: dados da equipe FaloComODono
 */
export const PartnerGuide = ({
  partnerName = 'Sua Empresa Parceira',
  partnerSlug = 'seu-slug',
  partnerToken = 'SEU-TOKEN-DE-ACESSO',
  supportWhatsapp = '+55 (31) 99131-6297',
  supportEmail = 'falocomodono@gmail.com',
  isGeneric = false,
}) => {
  const baseUrl = window.location.origin;
  const panelUrl = `${baseUrl}/parceiro/painel/${partnerToken}`;
  const salesUrl = `${baseUrl}/parceiro/${partnerSlug}`;

  const handlePrint = () => window.print();

  return (
    <div data-testid="partner-guide">
      {/* Botao de imprimir/salvar PDF (oculto na impressao) */}
      <div className="flex items-center justify-between mb-6 print:hidden">
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Guia do Parceiro</p>
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
            Tutorial passo a passo {isGeneric ? '(genérico)' : `· ${partnerName}`}
          </h2>
        </div>
        <Button onClick={handlePrint} className="bg-[#0A2647] hover:bg-[#0e3260] text-white gap-2" data-testid="print-guide-btn">
          <Printer className="w-4 h-4" />Baixar PDF / Imprimir
        </Button>
      </div>

      {/* CONTEUDO IMPRIMIVEL */}
      <div className="bg-white rounded-2xl shadow-lg p-8 sm:p-10 max-w-4xl mx-auto print:shadow-none print:p-0 print:max-w-none">

        <header className="mb-8">
          <span className="inline-block px-3 py-1 rounded-full bg-gradient-to-r from-[#C5A059] to-[#b8933f] text-[#0A2647] text-[11px] font-bold uppercase tracking-widest mb-3">
            ★ Guia do Parceiro · FaloComODono
          </span>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-[#0A2647] mb-2 leading-tight">
            Como cadastrar novos clientes e multiplicar sua base
          </h1>
          <p className="text-base text-slate-500">
            Tutorial direto ao ponto para {isGeneric ? 'empresas parceiras do FaloComODono' : <strong>{partnerName}</strong>}.
          </p>
        </header>

        <h2 className="text-2xl font-bold text-[#0A2647] mt-10 mb-4 pb-2 border-b-2 border-[#C5A059]">
          O que é o programa de Parceiros?
        </h2>
        <p className="text-sm text-slate-700 mb-3">
          Como parceira do FaloComODono, sua empresa pode <strong>cadastrar novos clientes diretamente</strong> no sistema e ainda ativar um <strong>efeito multiplicador</strong>: cada cliente cadastrado vira um divulgador automático que traz <em>mais clientes</em> de volta para a sua base.
        </p>

        <div className="bg-gradient-to-br from-[#FFF8E7] to-[#FFF1D6] border border-[#C5A059] p-5 rounded-xl my-5">
          <p className="text-[11px] font-bold text-[#C5A059] uppercase tracking-widest mb-2">★ Em 1 frase</p>
          <p className="text-sm text-[#5C4014] leading-relaxed">
            Você cadastra 1 cliente → ele usa o FaloComODono → a página de agradecimento dele divulga o nosso link → quem se cadastrar por esse caminho <strong>também entra na sua base de comissionamento</strong>.
          </p>
        </div>

        <h2 className="text-2xl font-bold text-[#0A2647] mt-10 mb-4 pb-2 border-b-2 border-[#C5A059]">
          3 caminhos para trazer novos clientes
        </h2>
        <p className="text-sm text-slate-700 mb-4">
          Você escolhe a estratégia certa pra cada situação. Os três caminhos geram comissão recorrente pra você (split automático no Asaas):
        </p>

        <div className="grid sm:grid-cols-3 gap-3 mb-5">
          <div className="border-2 border-blue-200 bg-blue-50/40 rounded-xl p-4">
            <div className="text-[10px] font-bold text-blue-700 uppercase tracking-wider mb-1">Caminho 1</div>
            <p className="text-sm font-bold text-[#0A2647] mb-2">📣 Link público de vendas</p>
            <p className="text-xs text-slate-700 leading-relaxed mb-2">
              Você compartilha seu link <strong>exclusivo</strong>. O próprio cliente entra, escolhe o plano e paga.
            </p>
            <p className="text-[11px] text-blue-800 font-semibold">Ideal para: divulgação em massa, redes sociais, indicações.</p>
          </div>
          <div className="border-2 border-emerald-200 bg-emerald-50/40 rounded-xl p-4">
            <div className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider mb-1">Caminho 2</div>
            <p className="text-sm font-bold text-[#0A2647] mb-2">🚀 Implantar Cliente</p>
            <p className="text-xs text-slate-700 leading-relaxed mb-2">
              Você cadastra e configura tudo pelo cliente, ele recebe <strong>link de pagamento Asaas em nome dele</strong> + 7 dias grátis.
            </p>
            <p className="text-[11px] text-emerald-800 font-semibold">Ideal para: cliente que precisa de ajuda na implantação ou prefere falar com você.</p>
          </div>
          <div className="border-2 border-amber-200 bg-amber-50/40 rounded-xl p-4">
            <div className="text-[10px] font-bold text-amber-700 uppercase tracking-wider mb-1">Caminho 3</div>
            <p className="text-sm font-bold text-[#0A2647] mb-2">💰 Patrocinar Cliente</p>
            <p className="text-xs text-slate-700 leading-relaxed mb-2">
              <strong>Você banca</strong> o cliente (R$ X/mês na sua conta Asaas), ele recebe Plano 5★ de cortesia.
            </p>
            <p className="text-[11px] text-amber-800 font-semibold">Ideal para: presentear cliente VIP, fidelizar ou fechar contrato comercial.</p>
          </div>
        </div>

        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs text-slate-600 mb-5">
          <strong className="text-[#0A2647]">📍 Onde encontrar cada caminho no painel:</strong>
          <ul className="mt-2 space-y-1 list-disc pl-5">
            <li><strong>Caminho 1:</strong> Aba "Dashboard" → seu link exclusivo já fica visível pra copiar.</li>
            <li><strong>Caminho 2:</strong> Aba "Implantar Cliente" → botão dourado.</li>
            <li><strong>Caminho 3:</strong> Aba "Patrocínios" → botão "Patrocinar novo cliente". (Tem um guia separado dedicado a esse caminho.)</li>
          </ul>
        </div>

        <h2 className="text-2xl font-bold text-[#0A2647] mt-10 mb-4 pb-2 border-b-2 border-[#C5A059]">
          Parte 1 — Como você cadastra um novo cliente
        </h2>

        {[
          {
            num: 1,
            title: 'Acesse seu Painel de Parceiro',
            body: (
              <>
                <p className="mb-2">Use o link único da {isGeneric ? 'empresa' : <strong>{partnerName}</strong>}. Ele tem o formato:</p>
                <code className="block bg-[#0A2647] text-[#C5A059] p-3 rounded-lg font-mono text-xs break-all">{panelUrl}</code>
                <p className="mt-2 text-xs text-slate-600">⚠️ <strong>Guarde esse link.</strong> Ele é o acesso direto, sem precisar de senha.</p>
              </>
            )
          },
          {
            num: 2,
            title: 'No painel, abra a aba "Minha Equipe"',
            body: (
              <>
                <p className="mb-2">Você terá 2 caminhos:</p>
                <ul className="list-disc pl-5 space-y-1 text-slate-700 text-sm">
                  <li><strong>Cadastrar Cliente Final</strong> — uma empresa (padaria, restaurante, clínica) que vai usar o FaloComODono no dia a dia</li>
                  <li><strong>Cadastrar Vendedor</strong> — alguém da equipe que sai pra rua vendendo em seu nome (recebe comissão direta via PIX)</li>
                </ul>
              </>
            )
          },
          {
            num: 3,
            title: 'Preencha os dados básicos do cliente',
            body: (
              <ul className="list-disc pl-5 space-y-1 text-slate-700 text-sm">
                <li>Nome do estabelecimento</li>
                <li>Email do dono</li>
                <li>WhatsApp do dono</li>
                <li>Plano escolhido (Básico, Pro, Empresarial, Elite ou 5 Estrelas)</li>
              </ul>
            )
          },
          {
            num: 4,
            title: 'Cliente paga a primeira mensalidade',
            body: (
              <>
                <p className="mb-2">Quando o pagamento confirma, o cliente:</p>
                <ul className="list-disc pl-5 space-y-1 text-slate-700 text-sm">
                  <li>Recebe acesso ao painel</li>
                  <li>Recebe email de boas-vindas com QR Code pronto pra imprimir</li>
                  <li>É <strong>vinculado automaticamente à sua base</strong> como Parceiro</li>
                </ul>
              </>
            )
          },
          {
            num: 5,
            title: 'Você recebe a comissão recorrente',
            body: <p>Sua empresa recebe split mensal de cada assinatura ativa. O extrato fica disponível no painel — aba "Extrato".</p>
          }
        ].map(step => (
          <div key={step.num} className="bg-slate-50 border-l-4 border-[#C5A059] p-4 my-3 rounded-r-xl">
            <p className="font-bold text-[#0A2647] mb-2 flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#0A2647] text-[#C5A059] font-extrabold text-sm">{step.num}</span>
              {step.title}
            </p>
            <div className="text-sm text-slate-700 leading-relaxed">{step.body}</div>
          </div>
        ))}

        <h2 className="text-2xl font-bold text-[#0A2647] mt-10 mb-4 pb-2 border-b-2 border-[#C5A059]">
          Parte 2 — O efeito multiplicador
        </h2>

        <p className="text-sm text-slate-700 mb-4">
          Cada cliente cadastrado carrega <strong>um link automático do FaloComODono</strong> em pontos onde os clientes finais dele veem todos os dias:
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 my-5">
          {[
            { num: 1, title: 'Você cadastra a Padaria do João', sub: 'Plano Pro · R$ 50 de comissão' },
            { num: 2, title: 'Padaria recebe avaliações', sub: 'Clientes usam o QR Code' },
            { num: 3, title: 'Página de agradecimento exibe nosso link', sub: '"Quer isso na sua empresa?"' },
            { num: 4, title: 'Pizzaria do bairro vê e assina', sub: '+1 cliente entra na sua base' }
          ].map(s => (
            <div key={s.num} className="bg-white border-2 border-slate-200 rounded-xl p-3 text-center">
              <div className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#C5A059] text-[#0A2647] font-extrabold text-sm mb-2">{s.num}</div>
              <p className="text-xs font-bold text-[#0A2647] leading-tight">{s.title}</p>
              <p className="text-[11px] text-slate-500 mt-1">{s.sub}</p>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-br from-[#FFF8E7] to-[#FFF1D6] border border-[#C5A059] p-5 rounded-xl my-5">
          <p className="text-[11px] font-bold text-[#C5A059] uppercase tracking-widest mb-2">💡 Por que isso funciona?</p>
          <p className="text-sm text-[#5C4014] leading-relaxed">
            Donos de negócio falam com outros donos de negócio. Quando o vizinho da Padaria do João recebe um QR Code de avaliação e gosta da experiência, ele entra no link e descobre o produto sozinho. <strong>O cliente trabalha como divulgador para você, sem que ele precise fazer nada.</strong>
          </p>
        </div>

        <h2 className="text-2xl font-bold text-[#0A2647] mt-10 mb-4 pb-2 border-b-2 border-[#C5A059]">
          Tabela de comissões por plano
        </h2>

        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-[#0A2647] text-white">
              <th className="text-left p-3 font-semibold text-[11px] uppercase tracking-wider">Plano</th>
              <th className="text-left p-3 font-semibold text-[11px] uppercase tracking-wider">Mensalidade do cliente</th>
              <th className="text-right p-3 font-semibold text-[11px] uppercase tracking-wider">Sua comissão</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['Básico', 'R$ 29,90', 'R$ 50,00', 'text-emerald-600'],
              ['Pro', 'R$ 39,90', 'R$ 60,00', 'text-emerald-600'],
              ['Empresarial', 'R$ 59,90', 'R$ 100,00', 'text-emerald-600'],
              ['Elite', 'R$ 99,90', 'R$ 150,00', 'text-emerald-600'],
              ['5 Estrelas ★', 'R$ 149,90', 'R$ 250,00', 'text-[#C5A059]'],
            ].map(([p, m, c, color]) => (
              <tr key={p} className="border-b border-slate-200">
                <td className="p-3 text-slate-700">{p}</td>
                <td className="p-3 text-slate-700">{m}</td>
                <td className={`p-3 text-right font-bold ${color}`}>{c}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-xs text-slate-500 mt-2">* Pagamento via Pix em parcela única, no momento em que o cliente paga. Sem retenção, valor cheio.</p>

        <h2 className="text-2xl font-bold text-[#0A2647] mt-10 mb-4 pb-2 border-b-2 border-[#C5A059]">
          Checklist rápido para o seu time de vendas
        </h2>

        {[
          {
            title: 'Antes de cadastrar o cliente',
            items: [
              'Já entendeu qual o problema do dono? (Notas baixas? Quer fidelizar?)',
              'Apresentou um plano que faz sentido para o tamanho do negócio',
              <>Mostrou a calculadora <code className="bg-[#FFF5E6] border border-[#F4D9A6] text-[#8B6914] px-1.5 py-0.5 rounded text-xs">{baseUrl}/calcular-perda</code> pra ele ver o ROI</>,
            ]
          },
          {
            title: 'Durante o cadastro',
            items: [
              'Confirme se o WhatsApp e Email estão corretos (alertas chegam por aí)',
              'Marque o plano correto (mudar depois exige novo link de cobrança)',
              'Avise o cliente que o pagamento vai chegar pelo Asaas',
            ]
          },
          {
            title: 'Depois do pagamento confirmado',
            items: [
              'Oriente o cliente a imprimir o QR Code e colocar no balcão/recepção',
              'Mostre o painel principal e como ler os alertas no WhatsApp',
              'Agende um check-in em 7 dias pra ver as primeiras avaliações chegando',
            ]
          }
        ].map(group => (
          <div key={group.title}>
            <h3 className="text-base font-bold text-[#0A2647] mt-5 mb-2">{group.title}</h3>
            <ul className="space-y-1.5">
              {group.items.map((item, i) => (
                <li key={i} className="text-sm text-slate-700 pl-7 relative leading-relaxed">
                  <span className="absolute left-0 top-0 text-emerald-500 font-bold text-base">✓</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        ))}

        <h2 className="text-2xl font-bold text-[#0A2647] mt-10 mb-4 pb-2 border-b-2 border-[#C5A059]">
          Dúvidas frequentes
        </h2>

        {[
          {
            q: 'Posso ter mais de 1 vendedor na minha empresa?',
            a: 'Sim. No painel há a opção "Cadastrar Vendedor". Cada vendedor recebe seu próprio link de venda e a comissão dele cai direto no PIX dele — você acompanha tudo no extrato consolidado.'
          },
          {
            q: 'O cliente cancelou. Eu perco a comissão?',
            a: 'Você recebe pelas mensalidades efetivamente pagas. Se o cliente cancelar no mês 6, você recebeu pelos 6 meses anteriores normalmente.'
          },
          {
            q: 'Posso oferecer desconto?',
            a: 'Cupons promocionais são gerenciados pela equipe central do FaloComODono. Se precisar de uma campanha especial pra fechar um lead grande, fale com a gente.'
          },
          {
            q: 'Como sei quando um novo cliente entrou pela "página de agradecimento"?',
            a: 'O extrato mostra a origem de cada cliente: "Direto" (você cadastrou) ou "Indireto" (veio pelo efeito multiplicador). Os indiretos contam exatamente igual aos diretos.'
          }
        ].map((faq, i) => (
          <div key={i} className="mb-4">
            <h3 className="text-base font-bold text-[#0A2647] mb-1">{faq.q}</h3>
            <p className="text-sm text-slate-700 leading-relaxed">{faq.a}</p>
          </div>
        ))}

        {!isGeneric && partnerSlug !== 'seu-slug' && (
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 mt-8">
            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-2">📍 Seus links de venda</p>
            <div className="space-y-2 text-xs">
              <div>
                <span className="text-slate-500 block mb-1">Link da landing pública (mande pra leads):</span>
                <code className="block bg-white border border-slate-200 p-2 rounded font-mono break-all">{salesUrl}</code>
              </div>
              <div>
                <span className="text-slate-500 block mb-1">Painel administrativo:</span>
                <code className="block bg-white border border-slate-200 p-2 rounded font-mono break-all">{panelUrl}</code>
              </div>
            </div>
          </div>
        )}

        <div className="bg-gradient-to-br from-[#FFF8E7] to-[#FFF1D6] border border-[#C5A059] p-5 rounded-xl mt-8">
          <p className="text-[11px] font-bold text-[#C5A059] uppercase tracking-widest mb-2">📱 Suporte direto</p>
          <p className="text-sm text-[#5C4014]">
            Dúvidas? Chama no WhatsApp: <strong>{supportWhatsapp}</strong> · Email: <strong>{supportEmail}</strong>
          </p>
        </div>

        <div className="text-center mt-10 pt-6 border-t border-slate-200">
          <p className="text-xs text-slate-500">FaloComODono · Sua base trabalhando 24h, 7 dias por semana</p>
          <p className="text-sm font-bold text-[#0A2647] mt-1">www.falocomodono.com.br</p>
        </div>
      </div>
    </div>
  );
};

export default PartnerGuide;
